import type { PointerEvent as ReactPointerEvent } from 'react'

import type { CollagePhoto } from '../../lib/collageTypes'

export type PointerHandlers = {
  onPointerDown: (e: ReactPointerEvent<HTMLElement>) => void
  onPointerMove: (e: ReactPointerEvent<HTMLElement>) => void
  onPointerUp: (e: ReactPointerEvent<HTMLElement>) => void
  onPointerCancel: (e: ReactPointerEvent<HTMLElement>) => void
}

const TILE_CONTROL_BUTTON_CLASS =
  'absolute flex size-6 items-center justify-center rounded-full bg-surface-container-lowest/90 text-on-surface opacity-0 shadow transition-opacity group-hover:opacity-100 group-focus-within:opacity-100'

type CollageTileProps = {
  photo: CollagePhoto
  heightPx: number
  isDragSource: boolean
  isDropTarget: boolean
  dragHandleHandlers: PointerHandlers
  onRemove: (photoId: string) => void
  registerTileEl: (photoId: string, el: HTMLDivElement | null) => void
  registerImgEl: (photoId: string, el: HTMLImageElement | null) => void
}

export default function CollageTile({
  photo,
  heightPx,
  isDragSource,
  isDropTarget,
  dragHandleHandlers,
  onRemove,
  registerTileEl,
  registerImgEl,
}: CollageTileProps) {
  return (
    <div
      ref={(el) => registerTileEl(photo.id, el)}
      data-tile-id={photo.id}
      className={`group relative shrink-0 overflow-hidden rounded-md border bg-transparent transition-[opacity,box-shadow] ${
        isDropTarget ? 'border-primary-container ring-2 ring-primary-container' : 'border-outline-variant/20'
      } ${isDragSource ? 'opacity-40' : ''}`}
      style={{ height: heightPx, aspectRatio: `${photo.naturalWidth} / ${photo.naturalHeight}` }}
    >
      <img
        ref={(el) => registerImgEl(photo.id, el)}
        src={photo.url}
        alt={photo.file.name}
        draggable={false}
        className="h-full w-full select-none object-cover"
      />

      <button
        type="button"
        title="Remove photo"
        aria-label={`Remove ${photo.file.name}`}
        onClick={() => onRemove(photo.id)}
        className={`${TILE_CONTROL_BUTTON_CLASS} right-1.5 top-1.5 hover:text-error`}
      >
        <span className="material-symbols-outlined text-base">close</span>
      </button>

      <button
        type="button"
        title="Drag to reorder"
        aria-label={`Drag to reorder ${photo.file.name}`}
        style={{ touchAction: 'none' }}
        className={`${TILE_CONTROL_BUTTON_CLASS} left-1.5 top-1.5 cursor-grab active:cursor-grabbing`}
        {...dragHandleHandlers}
      >
        <span className="material-symbols-outlined text-base">drag_indicator</span>
      </button>
    </div>
  )
}
